// frontend/src/services/authService.ts
import {
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    User as FirebaseUser,
    sendPasswordResetEmail,
    updateProfile,
    EmailAuthProvider,
    reauthenticateWithCredential
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { UserRole } from '../types';

export interface AuthUser {
    uid: string;
    username?: string;
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
    rol_usr?: UserRole;
    id_usuario?: string;
    nombre_completo?: string;
    privilegios?: any;
    porcentaje_comision?: number;
    codigo_vendedora?: string | null;
}

class AuthService {
    /**
     * Iniciar sesión con API REST (alternativa para desarrollo)
     */
    async loginWithREST(email: string, password: string): Promise<AuthUser> {
        const API_KEY = import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyCjK-_qmGbAurofUyg-2uw4_1V9Po-OE58";

        try {
            const response = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${API_KEY}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    password,
                    returnSecureToken: true,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = this.mapRestApiError(errorData.error?.message || 'Error de autenticación');
                throw new Error(errorMessage);
            }

            const data = await response.json();

            // Guardar el token en localStorage
            localStorage.setItem('authToken', data.idToken);
            localStorage.setItem('userEmail', data.email);
            localStorage.setItem('userId', data.localId);

            return {
                uid: data.localId,
                email: data.email,
                displayName: data.displayName || null,
                photoURL: null,
            };
        } catch (error: any) {
            console.error('Error en loginWithREST:', error);
            throw error;
        }
    }

    /**
     * Iniciar sesión con email y contraseña
     */
    async login(email: string, password: string): Promise<AuthUser> {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            return await this.mapFirebaseUserAsync(userCredential.user);
        } catch (error: any) {
            console.error('Error en login Firebase SDK:', error);

            // Si falla con error de dominio o protocolo, intentar con REST API
            if (error.code === 'auth/invalid-credential' ||
                error.message?.includes('protocol') ||
                error.message?.includes('frame') ||
                error.message?.includes('origin')) {
                console.log('🔄 Intentando con API REST como alternativa...');
                return await this.loginWithREST(email, password);
            }

            throw this.handleAuthError(error);
        }
    }

    /**
     * Verificar contraseña de la sesión actual sin alterar tokens innecesariamente
     * Útil para doble factor de autenticación local.
     */
    async verifyPassword(password: string): Promise<boolean> {
        const user = this.getCurrentUser();
        const email = user?.email || localStorage.getItem('userEmail');

        if (!email) {
            throw new Error('No se encontró el email del usuario logueado');
        }

        try {
            if (user) {
                const credential = EmailAuthProvider.credential(email, password);
                await reauthenticateWithCredential(user, credential);
                return true;
            } else {
                // Fallback para REST api
                await this.loginWithREST(email, password);
                return true;
            }
        } catch (error: any) {
            console.error('Error verificando contraseña:', error);
            if (error.code === 'auth/wrong-password' || error.message?.includes('INVALID_PASSWORD')) {
                return false;
            }
            throw this.handleAuthError(error);
        }
    }

    /**
     * Cerrar sesión
     */
    async logout(): Promise<void> {
        try {
            await signOut(auth);
        } catch (error: any) {
            console.error('Error al cerrar sesión con Firebase:', error);
        } finally {
            // Limpiar localStorage independientemente del resultado
            localStorage.removeItem('authToken');
            localStorage.removeItem('userEmail');
            localStorage.removeItem('userId');
        }
    }

    /**
     * Obtener el usuario actual
     */
    getCurrentUser(): FirebaseUser | null {
        return auth.currentUser;
    }

    /**
     * Verificar si está autenticado (usando localStorage como respaldo)
     */
    isAuthenticated(): boolean {
        const token = localStorage.getItem('authToken');
        return !!token || !!this.getCurrentUser();
    }

    /**
     * Obtener el token del usuario actual
     */
    async getCurrentToken(): Promise<string | null> {
        // Primero intentar obtener un token fresco de Firebase
        const user = this.getCurrentUser();
        if (user) {
            try {
                // Forzar refresh del token para asegurar que es válido
                const token = await user.getIdToken(true);
                // Actualizar localStorage con el token fresco
                localStorage.setItem('authToken', token);
                return token;
            } catch (error) {
                console.error('Error obteniendo token de Firebase:', error);
            }
        }

        // Si Firebase falla, intentar con localStorage como respaldo
        const storedToken = localStorage.getItem('authToken');
        if (storedToken) {
            console.warn('Usando token almacenado (puede estar expirado)');
            return storedToken;
        }

        return null;
    }

    /**
     * Observador de cambios en el estado de autenticación
     */
    onAuthStateChange(callback: (user: AuthUser | null) => void): () => void {
        return onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                const authUser = await this.mapFirebaseUserAsync(firebaseUser);
                callback(authUser);
            } else {
                callback(null);
            }
        });
    }

    /**
     * Enviar correo de recuperación de contraseña
     */
    async resetPassword(email: string): Promise<void> {
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (error: any) {
            throw this.handleAuthError(error);
        }
    }

    /**
     * Actualizar perfil del usuario
     */
    async updateUserProfile(displayName: string, photoURL?: string): Promise<void> {
        const user = this.getCurrentUser();
        if (!user) throw new Error('No hay usuario autenticado');

        try {
            await updateProfile(user, {
                displayName,
                ...(photoURL && { photoURL })
            });
        } catch (error: any) {
            throw this.handleAuthError(error);
        }
    }

    /**
     * Mapear usuario de Firebase a nuestro formato
     */
    private async mapFirebaseUserAsync(firebaseUser: FirebaseUser): Promise<AuthUser> {
        // Obtener el token para acceder a los custom claims y hacer peticiones
        const token = await firebaseUser.getIdToken();
        const tokenResult = await firebaseUser.getIdTokenResult();
        const role = tokenResult.claims.role as UserRole | undefined;

        try {
            // Obtener datos completos del usuario desde el backend
            const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
            const response = await fetch(`${API_URL}/users/me`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const result = await response.json();
                const userData = result.data;

                return {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email,
                    displayName: firebaseUser.displayName,
                    photoURL: firebaseUser.photoURL,
                    rol_usr: userData.rol_usr || role,
                    id_usuario: userData.id_usuario,
                    nombre_completo: userData.nombre_completo || firebaseUser.displayName || undefined,
                    privilegios: userData.privilegios || undefined,
                    porcentaje_comision: userData.porcentaje_comision || 0,
                    codigo_vendedora: userData.codigo_vendedora
                };
            }
        } catch (error) {
            console.error('Error obteniendo datos completos del usuario:', error);
        }

        // Fallback: datos básicos de Firebase
        return {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            rol_usr: role,
            id_usuario: firebaseUser.uid,
            nombre_completo: firebaseUser.displayName || undefined
        };
    }



    /**
     * Mapear errores de la API REST
     */
    private mapRestApiError(errorMessage: string): string {
        switch (errorMessage) {
            case 'EMAIL_NOT_FOUND':
                return 'No existe una cuenta con este correo electrónico';
            case 'INVALID_PASSWORD':
                return 'Contraseña incorrecta';
            case 'USER_DISABLED':
                return 'Esta cuenta ha sido deshabilitada';
            case 'TOO_MANY_ATTEMPTS_TRY_LATER':
                return 'Demasiados intentos fallidos. Intenta más tarde';
            case 'INVALID_EMAIL':
                return 'Correo electrónico inválido';
            default:
                return errorMessage || 'Error de autenticación';
        }
    }

    /**
     * Manejar errores de Firebase Auth
     */
    private handleAuthError(error: any): Error {
        let message = 'Error en la autenticación';

        switch (error.code) {
            case 'auth/invalid-email':
                message = 'El correo electrónico no es válido';
                break;
            case 'auth/user-disabled':
                message = 'Esta cuenta ha sido deshabilitada';
                break;
            case 'auth/user-not-found':
                message = 'No existe una cuenta con este correo';
                break;
            case 'auth/wrong-password':
                message = 'Contraseña incorrecta';
                break;
            case 'auth/invalid-credential':
                message = 'Credenciales inválidas';
                break;
            case 'auth/email-already-in-use':
                message = 'El correo ya está en uso';
                break;
            case 'auth/weak-password':
                message = 'La contraseña es muy débil';
                break;
            case 'auth/network-request-failed':
                message = 'Error de conexión. Verifica tu internet';
                break;
            case 'auth/too-many-requests':
                message = 'Demasiados intentos. Intenta más tarde';
                break;
            default:
                message = error.message || 'Error desconocido';
        }

        return new Error(message);
    }
}

export const authService = new AuthService();
export default authService;